import { useEffect } from "react";

import { useRoleProfile } from "@/lib/use-role-profile";

/**
 * Sync the document's tab icon + title to the active partner's brand.
 *
 * Mounted once near the root of the authenticated tree (AppShell).
 * Watches the loaded role-profile's ``primary_partner.brand_config``
 * and ``primary_partner.name``, then:
 *
 *   - rewrites every ``<link rel="icon">`` href to
 *     ``brand_config.logo_url`` when present (partner-branded tab
 *     icon - Finterra default falls through when nothing is set);
 *   - sets ``document.title`` to the partner name so the browser tab
 *     shows whose admin console is open.
 *
 * Bare React (no react-helmet) - the favicon swap is a one-line DOM
 * mutation and we already control the host index.html. On unmount we
 * restore the original href + title so a sign-out leaves the tab in
 * the same state Vite's dev server saw on first paint.
 */
export function useFavicon(): void {
  const profile = useRoleProfile();
  const logoUrl = profile?.primary_partner?.brand_config?.logo_url;
  const partnerName = profile?.primary_partner?.name;

  useEffect(() => {
    const links = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
    const originalHrefs = links.map((el) => el.getAttribute("href"));
    const originalTitle = document.title;

    if (logoUrl) {
      for (const link of links) {
        link.setAttribute("href", logoUrl);
        // Drop a stale ``type="image/jpeg"`` so the browser sniffs
        // the new url's content-type - partners may upload PNG/SVG/
        // ICO and the static type would mislabel the resource.
        link.removeAttribute("type");
      }
    }
    if (partnerName) {
      document.title = partnerName;
    }

    return () => {
      for (let i = 0; i < links.length; i += 1) {
        const link = links[i]!;
        const href = originalHrefs[i];
        if (href) link.setAttribute("href", href);
      }
      document.title = originalTitle;
    };
  }, [logoUrl, partnerName]);
}
